/**
 * MaintenanceLogView — read-only maintenance log preview modal.
 * Grid fact layout with transfer-modal field typography.
 */

import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { DeviceIDBadge } from '../../../shared/components/common/Badges';
import ModalCloseButton from '../../../shared/components/modals/ModalCloseButton';
import useBodyScrollLock from '../../../shared/hooks/useBodyScrollLock';
import { formatHoursSpent } from '../../../utils/numberUtils';
import {
  formatMaintenanceDate,
  formatMaintenanceDateTime,
  getMaintenanceTimeliness,
  getMaintenanceTypeLabel,
} from '../utils/maintenanceUtils';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import './MaintenanceLogView.css';

const MaintenanceLogView = ({
  maintenanceLogId,
  token,
  onClose,
  onNavigateToDeviceView,
  onNavigateToMaintenanceHistory,
}) => {
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useBodyScrollLock(true);

  const fetchLog = useCallback(async () => {
    if (!maintenanceLogId || !token) return;
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(API_ENDPOINTS.MAINTENANCE.DETAIL(maintenanceLogId), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLog(response.data);
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to load maintenance details';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [maintenanceLogId, token]);

  useEffect(() => {
    fetchLog();
  }, [fetchLog]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) onClose?.();
  };

  const renderBody = () => {
    if (loading) {
      return <div className="maintenance-log-view__loading">Loading maintenance details…</div>;
    }

    if (error || !log) {
      return (
        <div className="maintenance-log-view__error">
          {error || 'Maintenance log not found.'}
        </div>
      );
    }

    const timeliness = getMaintenanceTimeliness(log.maintenance_date, log.due_date);
    const loggedAt = formatMaintenanceDateTime(log.created_at);
    const completedAt = formatMaintenanceDateTime(log.maintenance_date);
    const typeKey = (log.maintenance_type || '').toLowerCase();

    let completionLabel = 'N/A';
    let completionClass = 'maintenance-log-view__fact-value--na';
    if (timeliness.isOnTime === true) {
      completionLabel = 'On-time';
      completionClass = 'maintenance-log-view__fact-value--on-time';
    } else if (timeliness.isOnTime === false) {
      completionLabel = `${timeliness.daysLate} day${timeliness.daysLate === 1 ? '' : 's'} late`;
      completionClass = 'maintenance-log-view__fact-value--late';
    }

    return (
      <>
        <div className="maintenance-log-view__summary">
          <span className="maintenance-type-badge" data-type={typeKey}>
            {getMaintenanceTypeLabel(log.maintenance_type)}
          </span>
          <div className="maintenance-log-view__device">
            <span className="maintenance-log-view__device-name">{log.device_name || 'Device'}</span>
            {log.device_identifier && (
              <DeviceIDBadge deviceId={log.device_identifier} size="small" />
            )}
          </div>
        </div>

        <div className="maintenance-log-view__facts">
          <div className="maintenance-log-view__fact">
            <span className="maintenance-log-view__fact-label">Department</span>
            <span className="maintenance-log-view__fact-value">
              {log.department_icon ? `${log.department_icon} ` : ''}
              {log.department || 'N/A'}
            </span>
          </div>
          <div className="maintenance-log-view__fact">
            <span className="maintenance-log-view__fact-label">Performed by</span>
            <span className="maintenance-log-view__fact-value">
              {log.performed_by_name || 'Unknown'}
              {log.performed_by_employee_id ? ` (${log.performed_by_employee_id})` : ''}
            </span>
          </div>
          <div className="maintenance-log-view__fact">
            <span className="maintenance-log-view__fact-label">Completed</span>
            <span className="maintenance-log-view__fact-value">
              {completedAt.date}
              {completedAt.time ? ` · ${completedAt.time}` : ''}
            </span>
          </div>
          <div className="maintenance-log-view__fact">
            <span className="maintenance-log-view__fact-label">Hours</span>
            <span className="maintenance-log-view__fact-value">{formatHoursSpent(log.hours_spent)}</span>
          </div>
          {log.due_date && (
            <div className="maintenance-log-view__fact">
              <span className="maintenance-log-view__fact-label">Due (SLA)</span>
              <span className="maintenance-log-view__fact-value">{formatMaintenanceDate(log.due_date)}</span>
            </div>
          )}
          {log.due_date && (
            <div className="maintenance-log-view__fact">
              <span className="maintenance-log-view__fact-label">Completion</span>
              <span className={`maintenance-log-view__fact-value ${completionClass}`}>{completionLabel}</span>
            </div>
          )}
          {log.next_due_date && (
            <div className="maintenance-log-view__fact">
              <span className="maintenance-log-view__fact-label">Next due</span>
              <span className="maintenance-log-view__fact-value">{formatMaintenanceDate(log.next_due_date)}</span>
            </div>
          )}
        </div>

        <div className="maintenance-log-view__section">
          <h4 className="maintenance-log-view__section-title">Work description</h4>
          <p className="maintenance-log-view__description">
            {log.description?.trim() || 'No description provided.'}
          </p>
        </div>

        <div className="maintenance-log-view__meta">
          Logged {loggedAt.date}
          {loggedAt.time ? ` · ${loggedAt.time}` : ''}
        </div>
      </>
    );
  };

  const modal = (
    <div className="modal-overlay maintenance-log-view-overlay" onClick={handleOverlayClick}>
      <div
        className="maintenance-log-view-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="maintenance-log-view-title"
        aria-busy={loading}
      >
        <div className="maintenance-log-view__header">
          <h2 id="maintenance-log-view-title">Maintenance log</h2>
          <ModalCloseButton onClick={onClose} title="Close" />
        </div>

        <div className="maintenance-log-view__body">
          {renderBody()}
        </div>

        {!loading && (
          <div className="maintenance-log-view__footer">
            <button type="button" className="action-btn action-secondary" onClick={onClose}>
              Close
            </button>
            {log && typeof onNavigateToDeviceView === 'function' && log.device_id && (
              <button
                type="button"
                className="action-btn action-secondary"
                onClick={() => onNavigateToDeviceView(log.device_id)}
              >
                View device
              </button>
            )}
            {log && typeof onNavigateToMaintenanceHistory === 'function' && log.device_identifier && (
              <button
                type="button"
                className="action-btn action-secondary"
                onClick={() => onNavigateToMaintenanceHistory({ device_identifier: log.device_identifier })}
              >
                Full history
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default MaintenanceLogView;
